import React from "react";

import { Grid } from "@material-ui/core";

import {
  Button,
  Edit,
  FormDataConsumer,
  maxLength,
  minLength,
  regex,
  required,
  SimpleForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useEditController,
  useRedirect,
} from "react-admin";

import { useUser } from "../../../authenticatorApp/UserContext";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { AclType, REGEX_PHONE } from "../../../utils/constants";
import { getBackPath } from "../../../utils/utils";
import { ValidateSaveButton } from "./ValidateSaveButton";
import { DeleteAccountButton, DeleteYardButton } from "./DeepDeleteButton";
import { CustomerAside } from "./CustomerAside";
import { usePasskeyCheck } from "../../../hooks/usePasskeyCheck";
import FingerprintIcon from "@material-ui/icons/Fingerprint";
import { useAuth } from "react-oidc-context";
import awsExports from "../../../aws-exports";
import { redirectToPasskeySetup } from "../../../utils/passkeyUtils";

const validateRequired = required();
const validatePhone = [
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];

const CustomerEditForm = ({ record, ...props }) => {
  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <TextInput
              source="firstName"
              label="First Name"
              variant="outlined"
              fullWidth
              validate={validateRequired}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              source="lastName"
              label="Last Name"
              variant="outlined"
              fullWidth
              validate={validateRequired}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              source="email"
              type="email"
              variant="outlined"
              fullWidth
              parse={(v) => (v !== "" ? v.toLowerCase() : undefined)}
              disabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              source="phone"
              type="tel"
              variant="outlined"
              fullWidth
              validate={validatePhone}
            />
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const CustomerEditActions = (props) => {
  const auth = useAuth();
  const { hasPasskey, isChecking } = usePasskeyCheck(
    auth.user?.access_token,
    auth.isAuthenticated,
    awsExports.aws_project_region,
  );
  return (
    <TopToolbar>
      <Button
        label="Passkey"
        onClick={redirectToPasskeySetup}
        disabled={isChecking || hasPasskey}
      >
        <FingerprintIcon />
      </Button>
      <BackButton {...props} />
    </TopToolbar>
  );
};
const CustomerEditToolbar = ({
  record,
  appUser,
  setAppUser,
  isMultiTenant,
  backPath,
  ...props
}) => {
  return (
    <Toolbar style={{ justifyContent: "space-between" }} {...props}>
      <ValidateSaveButton {...props} record={record} backPath={backPath} />
      {isMultiTenant() && (
        <DeleteYardButton
          record={record}
          appUser={appUser}
          setAppUser={setAppUser}
          backPath={backPath}
        />
      )}
      <DeleteAccountButton record={record} backPath={backPath} />
    </Toolbar>
  );
};

export const CustomerEdit = (props) => {
  const { appUser, setAppUser, isAuthorized, isMultiTenant } = useUser();
  const { basePath, record } = useEditController(props);
  const redirect = useRedirect();
  const backPath = isAuthorized(AclType.USERS) ? getBackPath(props) : ["/"];

  if (appUser && record && appUser.id !== record.id) {
    if (!isAuthorized(AclType.USERS)) redirect("show", basePath, "invalid");

    // customer can view but not edit other user's profiles.
    redirect("show", basePath, record.id);
  }

  return (
    <Edit
      {...props}
      title={<ForceTitle title="Edit User" />}
      mutationMode="pessimistic"
      actions={<CustomerEditActions backPath={backPath} />}
      aside={<CustomerAside {...props} />}
    >
      <SimpleForm
        variant="outlined"
        submitOnEnter={false}
        toolbar={
          <CustomerEditToolbar
            record={record}
            appUser={appUser}
            setAppUser={setAppUser}
            isMultiTenant={isMultiTenant}
            backPath={backPath}
          />
        }
      >
        <CustomerEditForm record={record} />
      </SimpleForm>
    </Edit>
  );
};
